import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import { Checkbox } from '@praetorian-chariot/ui';
import { VirtualItem } from '@tanstack/react-virtual';
import React, { forwardRef, ReactNode, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import { Dropdown } from '@/components/Dropdown';
import { Loader } from '@/components/Loader';
import { CELL_WIDTHS, ROW_HEIGHT } from '@/components/table/constants';
import { TableCellContent } from '@/components/table/TableCellContent';
import {
  CellAlignment,
  Column,
  Columns,
  InternalTData,
  TableBodyHoverProps,
  TableProps,
} from '@/components/table/types';
import { applyColumnWidth } from '@/components/table/utils/columnWidth';
import { cn } from '@/utils/classname';

interface TableBodyProps<TData> extends TableBodyHoverProps {
  selectedRows: string[];
  data: InternalTData<TData>[];
  rowActions?: TableProps<TData>['rowActions'];
  rowData: InternalTData<TData>;
  rowIndex: number;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore-error
  virtualRow: VirtualItem<Element>;
  isChecked: boolean;
  handleRowClick: (
    idx: number,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void;
  enableCheckbox: boolean;
  isLoading: boolean;
  columns: Columns<TData>;
  isClickable: boolean;
  expandedGroups: string[];
  toggleExpandedGroup: (group?: string) => void;
  groupBy?: TableProps<TData>['groupBy'];
  rowClassName?: (rowData: TData) => string;
  sort?: TableProps<TData>['sort'];
  inspecting: number | null;
  rowHeight?: number;
  tabIndex: number;
  isLastRow?: boolean;
}

export const TableBody = forwardRef(function TableBody<TData>(
  props: TableBodyProps<TData>,
  ref: React.ForwardedRef<HTMLTableRowElement>
) {
  const {
    selectedRows,
    data,
    rowActions,
    rowData,
    rowIndex,
    virtualRow,
    isChecked,
    handleRowClick,
    enableCheckbox,
    isLoading,
    columns,
    isClickable,
    expandedGroups,
    toggleExpandedGroup,
    rowClassName,
    sort,
    inspecting,
    rowHeight,
    tabIndex,
    isLastRow,
    hoveredRowIndex,
    setHoveredRowIndex,
  } = props;

  // CRITICAL: All hooks MUST be called before any early returns
  // Hover state is now controlled by parent Table component
  const handleMouseEnter = useCallback(() => {
    if (!isClickable) return;
    setHoveredRowIndex(rowIndex);
  }, [isClickable, rowIndex, setHoveredRowIndex]);

  const handleMouseLeave = useCallback(() => {
    if (!isClickable) return;
    setHoveredRowIndex(null);
  }, [isClickable, setHoveredRowIndex]);

  // Null safety guard - early return AFTER hooks
  if (!rowData) {
    console.warn('TableBody: Received undefined rowData, skipping render');
    return null;
  }

  const isFirstRow = rowIndex === 0;

  if (rowData?._type === 'colgroup') {
    const GroupName = rowData?._label || 'Group';

    const isExpanded = expandedGroups.includes(GroupName);
    return (
      <tr
        ref={ref}
        tabIndex={tabIndex}
        className="border-default bg-layer2 focus-visible:bg-chariot-background-secondary cursor-pointer border-t text-sm font-semibold"
        key={`row-${rowIndex}`}
        style={{
          height: `${virtualRow.size}px`,
        }}
        onClick={() => toggleExpandedGroup(GroupName)}
      >
        <th className="">
          <ChevronDownIcon
            className={cn('m-auto size-4', !isExpanded && 'rotate-180')}
          />
        </th>
        <th className="text-left" colSpan={columns.length + 2} scope="colgroup">
          <Loader isLoading={isLoading}>
            <div className="flex flex-row items-center space-x-2">
              {rowData?._icon}
              <p>{GroupName}</p>
            </div>
          </Loader>
        </th>
      </tr>
    );
  }

  return (
    <tr
      ref={ref}
      tabIndex={tabIndex}
      key={`row-${rowIndex}`}
      data-testid={`row-${rowData?._key}`}
      data-row-index={rowIndex}
      data-index={virtualRow.index}
      style={{
        height: `${virtualRow.size}px`,
      }}
      className={twMerge(
        'transition-colors duration-150 focus-visible:outline-none',
        hoveredRowIndex === rowIndex && 'bg-chariot-background-secondary',
        inspecting === Number(rowData?._idx) &&
          'is-selected group bg-chariot-stroke text-chariot-text-primary',
        isChecked && 'bg-chariot-background-secondary',
        isChecked && inspecting && 'bg-chariot-background-secondary',
        isClickable && 'cursor-pointer',
        isLastRow && 'border-chariot-stroke border-b',
        rowClassName && rowClassName(rowData)
      )}
      onClick={event => {
        // PHASE 1 - CHANGE 2: Null safety check for rowData._idx in row onClick
        if (!rowData || !rowData._idx) {
          return;
        }

        event.preventDefault();
        if (isClickable) {
          handleRowClick(Number(rowData._idx), event);
        } else if (enableCheckbox) {
          // Handle checkbox clicks even when row is not clickable
          const target = event.target as HTMLElement;
          const clickedCheckbox = target?.closest?.('[data-checkbox]');
          if (clickedCheckbox) {
            handleRowClick(Number(rowData._idx), event);
          }
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {enableCheckbox && (
        <Td
          data-checkbox
          data-testid="checkbox-cell"
          align="center"
          isFirstRow={isFirstRow}
          isLoading={isLoading}
          rowHeight={rowHeight}
          fixedWidth={CELL_WIDTHS.checkbox.fixedWidth}
          minWidth={CELL_WIDTHS.checkbox.minWidth}
          maxWidth={CELL_WIDTHS.checkbox.maxWidth}
          onClick={e => {
            // PHASE 1 - CHANGE 3: Null safety check for rowData._idx in checkbox onClick
            if (!rowData || !rowData._idx) {
              e.stopPropagation(); // Still prevent drawer
              return;
            }

            // Always prevent drawer from opening
            e.stopPropagation();

            // Manually trigger row click to handle selection
            // Because this <Td> has data-checkbox attribute, handleRowClick will
            // recognize this as a checkbox click and run selection logic
            handleRowClick(
              Number(rowData._idx),
              e as unknown as React.MouseEvent<HTMLTableRowElement, MouseEvent>
            );
          }}
          className="hover:bg-chariot-background-secondary/50 cursor-pointer transition-colors duration-150"
        >
          <Loader isLoading={isLoading}>
            <label
              className="cursor-pointer"
              tabIndex={0}
              onClick={e => {
                // Prevent default label behavior and let parent Td handle it
                e.preventDefault();
                // Don't stopPropagation - let it bubble to Td's onClick
              }}
            >
              <input
                type="checkbox"
                checked={isChecked}
                className="hidden"
                onChange={() => {}}
              />
              <Checkbox isChecked={isChecked} />
            </label>
          </Loader>
        </Td>
      )}
      {columns.map((col, colIndex) => {
        return (
          <Td
            key={`row-${rowIndex}-${colIndex}${String(col.id)}`}
            column={col}
            className={col.className}
            isFirstRow={isFirstRow}
            isLoading={isLoading}
            align={col.align}
            sort={sort}
            id={col.id as keyof TData}
            rowHeight={rowHeight}
          >
            <Loader isLoading={isLoading}>
              <TableCellContent
                inspecting={inspecting === Number(rowData?._idx)}
                col={col}
                item={rowData}
                selectedRowsData={selectedRows.map(i => data[Number(i)])}
                isLoading={col.isLoading ? col.isLoading(rowData) : false}
                onClick={
                  col.onClick ? () => col?.onClick?.(rowData) : undefined
                }
              />
            </Loader>
          </Td>
        );
      })}
      {rowActions &&
        (() => {
          const actions = rowActions(rowData);
          return (
            <Td
              align="center"
              isFirstRow={isFirstRow}
              isLoading={isLoading}
              rowHeight={rowHeight}
              fixedWidth={CELL_WIDTHS.actions.fixedWidth}
              minWidth={CELL_WIDTHS.actions.minWidth}
              maxWidth={CELL_WIDTHS.actions.maxWidth}
            >
              {actions.menu?.items && actions.menu.items.length > 0 && (
                <Loader isLoading={isLoading}>
                  <Dropdown
                    onClick={event => {
                      event.stopPropagation();
                    }}
                    startIcon={
                      <EllipsisVerticalIcon className="m-auto size-6" />
                    }
                    styleType="none"
                    {...actions}
                  />
                </Loader>
              )}
            </Td>
          );
        })()}
    </tr>
  );
}) as <TData>(
  props: TableBodyProps<TData> & {
    ref?: React.ForwardedRef<HTMLTableRowElement>;
  }
) => React.ReactElement;

function Td<TData>(props: {
  column?: Column<TData>; // NEW: Pass column config for proper width application
  id?: keyof TData;
  className?: string;
  align?: CellAlignment;
  sort?: TableProps<TData>['sort'];
  colSpan?: number;
  children: ReactNode;
  isFirstRow: boolean;
  isLoading: boolean;
  rowHeight?: number;
  onClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
  'data-testid'?: string;
  'data-checkbox'?: boolean;
  // Deprecated props (maintain backward compatibility)
  fixedWidth?: number;
  minWidth?: number;
  maxWidth?: number;
}) {
  // Use centralized width application if column provided
  const widthStyle = props.column
    ? applyColumnWidth(props.column, {})
    : {
        ...(props.fixedWidth && {
          width: props.fixedWidth,
          minWidth: props.fixedWidth,
          maxWidth: props.fixedWidth,
        }),
        ...(!props.fixedWidth &&
          props.minWidth && { minWidth: props.minWidth }),
        ...(!props.fixedWidth &&
          props.maxWidth && { maxWidth: props.maxWidth }),
      };

  // Determine if we should truncate or allow wrapping
  const shouldTruncate = !props.column?.wrapLines;

  return (
    <td
      colSpan={props.colSpan}
      className={cn(
        'relative max-w-full pr-1 pl-2 text-sm',
        shouldTruncate && 'truncate',
        props.align === 'center' && 'pr-3 text-center *:m-auto',
        props.align === 'right' && 'text-right',
        props.className
      )}
      style={{
        height: props.rowHeight ?? ROW_HEIGHT,
        ...widthStyle,
      }}
      onClick={props.onClick}
      data-testid={props['data-testid']}
      data-checkbox={props['data-checkbox'] || undefined}
    >
      <div
        className={cn([
          !props.isFirstRow &&
            'border-chariot-stroke absolute top-0 left-0 w-full border-t',
          'flex items-center',
        ])}
      />
      {props.children}
    </td>
  );
}
